/**
 * @architecture-header 1
 * @layer module
 * @domain notifications
 * @responsibility Load the current account's aggregated inbox through TanStack Query.
 * @state-owner TanStack Query
 * @allowed-imports app environment; contracts; TanStack Query
 * @forbidden routes; app bootstrap; direct Supabase client; Zustand entity copies
 * @public-api via modules/notifications/index.ts
 * @side-effects authenticated GET request outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useQuery } from '@tanstack/react-query';
import { type InboxItem, inboxItemSchema } from '@vispo/contracts';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';

const now = new Date().toISOString();
const demoInbox: InboxItem[] = [
  {
    id: 'sub-demo-1',
    type: 'submission',
    title: '王柏睿 — 一元一次方程式',
    subtitle: '已從週三數學 A 班繳交，等待批改',
    timestamp: now,
    isRead: false,
    isCompleted: false,
    isUrgent: false,
    meta: { classId: 'class-8a', className: '週三數學 A 班', studentId: 'student-wang' },
  },
  {
    id: 'overdue-demo-1',
    type: 'overdue',
    title: '比例式練習',
    subtitle: '國三總複習已超過期限',
    timestamp: new Date(Date.now() - 86_400_000).toISOString(),
    isRead: false,
    isCompleted: false,
    isUrgent: true,
    meta: { classId: 'class-7b', className: '國三總複習' },
  },
  {
    id: 'enroll-demo-1',
    type: 'enrollment',
    title: '陳思妤',
    subtitle: '已加入週三數學 A 班',
    timestamp: new Date(Date.now() - 172_800_000).toISOString(),
    isRead: true,
    isCompleted: false,
    isUrgent: false,
    meta: { classId: 'class-8a', className: '週三數學 A 班' },
  },
];

export const inboxKeys = {
  list: (activeContextId: string) => ['inbox', activeContextId] as const,
};

export function useInbox(activeContextId: string, enabled = true) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: inboxKeys.list(activeContextId),
    enabled,
    queryFn: async () => {
      if (environment.demoMode) return demoInbox;
      return z
        .array(inboxItemSchema)
        .parse(await environment.apiClient.request<unknown>('/api/inbox'));
    },
  });
}
